(() => {
  if (customElements.get('wsc-fury-diamond-gallery')) return;
  const imageSelector = 'img[data-wsc-fury-image]';
  const radioName = 'properties[Diamond Edition Upgrade]';

  // Decode off-screen without ever removing the photo currently on screen.
  function prepare(image, state) {
    const clone = image.cloneNode(true);
    const loading = clone.getAttribute('loading');
    clone.loading = 'eager';
    clone.setAttribute('fetchpriority', 'low');
    clone.srcset = image.dataset[`${state}Srcset`];
    clone.src = image.dataset[`${state}Src`];
    clone.alt = image.dataset[`${state}Alt`];
    clone.dataset.furyState = state;
    return new Promise((resolve, reject) => {
      let finished = false;
      const timer = setTimeout(() => finish(new Error('Image load timed out')), 20000);
      function finish(error) {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        clone.onload = clone.onerror = null;
        if (error) { reject(error); return; }
        if (loading === null) clone.removeAttribute('loading');
        else clone.setAttribute('loading', loading);
        const priority = image.getAttribute('fetchpriority');
        if (priority === null) clone.removeAttribute('fetchpriority');
        else clone.setAttribute('fetchpriority', priority);
        resolve({ image, clone });
      }
      clone.onload = () => {
        if (typeof clone.decode !== 'function') { finish(); return; }
        clone.decode().then(() => finish(), () => {
          if (clone.complete && clone.naturalWidth > 0) finish();
          else finish(new Error('Image decode failed'));
        });
      };
      clone.onerror = () => finish(new Error('Image download failed'));
      if (clone.complete && clone.naturalWidth > 0) clone.onload();
    });
  }

  customElements.define('wsc-fury-diamond-gallery', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info[data-wsc-fury-diamond-gallery]');
      if (!this.root || this.started) return;
      this.started = true;
      this.generation = 0;
      this.state = this.root.dataset.furyInitialState;
      this.onChange = (event) => {
        const input = event.target;
        if (input.name === radioName && input.checked) {
          if (input.value === 'No Diamond') this.setState('standard');
          else if (/^Add Diamond: /.test(input.value)) this.setState('diamond');
        } else if (input.matches('variant-selects select')) {
          // Dawn leaves the retiring picker in the DOM for 500ms.
          const picker = input.closest('variant-selects');
          if (picker.style.display === 'none') return;
          if (input.value === 'Diamond') this.setState('diamond');
          else if (input.value === 'No Diamond') this.setState('standard');
        }
      };
      this.observer = new MutationObserver((records) => {
        if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
          node.nodeType === 1 && (node.matches?.(imageSelector) || node.querySelector?.(imageSelector))
        ))) this.schedule();
      });
      this.onHide = () => { this.generation++; this.observer.disconnect(); };
      this.onShow = (event) => {
        this.observe();
        const restored = event.persisted ? this.root.querySelector(`input[name="${radioName}"]:checked`) : null;
        if (restored?.value === 'No Diamond') this.state = 'standard';
        else if (/^Add Diamond: /.test(restored?.value || '')) this.state = 'diamond';
        this.schedule();
      };
      this.root.addEventListener('change', this.onChange);
      window.addEventListener('pagehide', this.onHide);
      window.addEventListener('pageshow', this.onShow);
      this.observe();
      this.schedule();
    }

    surfaces() {
      // Dawn moves its modal to document.body during connectedCallback.
      const modal = document.getElementById(`ProductModal-${this.root.dataset.section}`);
      return modal && !this.root.contains(modal) ? [this.root, modal] : [this.root];
    }

    observe() {
      this.surfaces().forEach(surface => this.observer.observe(surface, { childList: true, subtree: true }));
    }

    setState(state) { this.state = state; this.schedule(); }

    schedule() {
      this.generation++;
      if (this.scheduled) return;
      this.scheduled = true;
      queueMicrotask(() => {
        this.scheduled = false;
        if (this.isConnected) this.reconcile();
      });
    }

    async reconcile() {
      const generation = this.generation;
      const state = this.state;
      this.observe();
      const targets = this.surfaces().flatMap(surface => Array.from(surface.querySelectorAll(imageSelector)))
        .filter(image => image.dataset.furyState !== state);
      if (!targets.length) return;
      try {
        const ready = await Promise.all(targets.map(image => prepare(image, state)));
        if (!this.isConnected || generation !== this.generation || state !== this.state ||
          ready.some(({ image }) => !this.surfaces().some(surface => surface.contains(image)))) return;
        this.observer.disconnect();
        // Synchronous replacements are painted together after all downloads finish.
        ready.forEach(({ image, clone }) => image.replaceWith(clone));
        this.observe();
      } catch (error) {
        if (generation === this.generation) console.warn('[Fury gallery] Kept current photos:', error.message);
      }
    }

    disconnectedCallback() {
      this.generation++;
      this.observer?.disconnect();
      this.root?.removeEventListener('change', this.onChange);
      window.removeEventListener('pagehide', this.onHide);
      window.removeEventListener('pageshow', this.onShow);
      this.started = false;
    }
  });
})();
